import {emotionStyled, tokens} from '@packages/utilities/styled';
import {Button} from '../Button';
import {HStack} from '../Stack/HStack';
import {Note} from '../Note';
import {Text} from '../Text';
import {hoverSelector} from '../../utils';

export const SelectWrapperView = emotionStyled('div')({
  display: 'flex',
  width: '100%',
});

export const SelectTriggerButtonView = emotionStyled(Button)(
  {
    cursor: 'default',
    padding: tokens.ui.select.control.buttonPadding,
    width: '100%',

    // Necessary to work around the additional <span> that Button uses that we don't want in Select
    '> span > span': {
      display: 'contents',
      textAlign: 'left',
      whiteSpace: 'initial',
    },

    '&:focus-visible, &[aria-expanded="true"]': {
      borderColor: 'transparent',
      outline: `1px solid ${tokens.colors.blueBorder}`,
      outlineOffset: '0px',
    },
  },
  {
    variants: {
      colorVariant: {
        default: {},
        secondary: {},
        black: {},
        blue: {},
        green: {},
        orange: {},
        pink: {},
        purple: {},
        red: {},
        white: {},
        primary: {},
        danger: {},
        success: {},
      },
      isError: {
        true: {
          borderColor: 'transparent',
          outline: `1px solid ${tokens.colors.redBorder}`,
          outlineOffset: '0px',
        },
      },
      shouldPositionInline: {
        true: {
          '&[aria-expanded="true"]': {
            outline: 'none',
          },
        },
      },
      _shouldUseLegacyAlignment: {
        true: {
          paddingLeft: '$2 !important',
        },
      },
      isBranded: {
        true: {
          // Uses `!important` because the `{colorVariant: 'default', variant: 'solid'}`
          // compound variant below also sets `padding` and is merged later in the cascade.
          // Same reason `_shouldUseLegacyAlignment` above uses `!important` on `paddingLeft`.
          padding: '$2 !important',
          // Descendant selector to beat each inner `Text`'s own emotion class — setting
          // font rules on the button/label classes directly ties on specificity (0,1,0) and
          // loses source-order to the inner `Text`. Targeting `span` raises specificity to
          // (0,1,1), which wins.
          span: {
            fontFamily: '$brandedBody',
            fontSize: '$brandedBody',
            lineHeight: '$brandedBody',
          },
        },
      },
      variant: {
        outline: {
          '&[aria-expanded="true"]': {
            background: 'transparent',
            boxShadow: 'none',
            outlineOffset: '1px',
          },
        },
        ghost: {},
        subtle: {
          '&[aria-expanded="true"]': {
            borderColor: 'transparent',
            outlineOffset: '1px',
          },
        },
        solid: {},
      },
    },
    compoundVariants: [
      {
        conditions: {
          variant: ['outline', 'ghost'],
          colorVariant: 'white',
        },
        css: {
          '&:focus-visible, &[aria-expanded="true"]': {
            outline: `1px solid ${tokens.colors.white}`,
          },
        },
      },
      {
        conditions: {colorVariant: 'default', variant: 'solid'},
        css: {
          background: '$uiSelectBackground',
          border: '$uiSelectBorder',
          boxShadow: '$uiSelect',
          padding: tokens.ui.select.control.padding,
          [hoverSelector]: {
            background: '$uiSelectBackgroundHover',
          },
        },
      },
      {
        conditions: {
          variant: 'outline',
          colorVariant: 'black',
        },
        css: {
          '&[aria-expanded="true"]': {
            background: tokens.colors.blackA10,
            boxShadow: 'none',
          },
        },
      },
    ],
  }
);
export const SelectTriggerButtonHStack = emotionStyled(HStack)({
  gap: tokens.ui.select.option.spacing,
});

export const SelectTriggerButtonLabel = emotionStyled(Text)({
  width: '100%',
  textAlign: 'start',
});

export const SELECT_MENU_MIN_WIDTH = 100;

export const SelectMenu = emotionStyled('div')(
  {
    background: '$uiMenuContentBackground',
    borderRadius: '$2',
    boxShadow: '$menu',
    color: '$textPrimary',
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    padding: tokens.ui.menu.content.padding,
    outline: '0',
    userSelect: 'none',
    // The width value will grow to be the same width as the trigger button
    // or the width specified in the `dropdownWidth` prop. (This is handled by Floating UI.)
    // This is an arbitrary minWidth value in the event that the trigger button's width is very small.
    minWidth: SELECT_MENU_MIN_WIDTH,
  },
  {
    variants: {
      shouldNotMatchTriggerWidth: {
        true: {
          width: 'fit-content',
        },
      },
    },
  }
);

export const SelectMenuOptionsContainer = emotionStyled('div')({
  overflowY: 'auto',
});

export const OptionView = emotionStyled('button')(
  {
    all: 'unset',
    boxSizing: 'border-box',
    width: '100%',
    display: 'block',
    padding: tokens.ui.select.option.padding,
    userSelect: 'none',
    background: 'transparent',
    color: '$textPrimary',
    lineHeight: '$body',
    fontSize: '$body',
    fontWeight: '$normal',
    letterSpacing: '$normal',
  },
  {
    variants: {
      isActive: {
        true: {
          background: '$uiBackgroundHover',
        },
      },
      isDisabled: {
        true: {
          opacity: tokens.opacities.disabled,
        },
      },
      shouldNavigateDisabledOptions: {
        true: {},
      },
      isBranded: {
        true: {
          // Descendant selector — see the matching comment on `SelectTriggerButtonView`.
          span: {
            fontFamily: '$brandedBody',
            fontSize: '$brandedBody',
            lineHeight: '$brandedBody',
          },
        },
      },
    },
    compoundVariants: [
      // When `shouldNavigateDisabledOptions` is `false`, we DON'T want to show a highlight when disabled options are hovered (and they can't be keyboard navigated)
      {
        conditions: {
          isActive: true,
          isDisabled: true,
        },
        css: {
          background: 'transparent',
        },
      },
      // When `shouldNavigateDisabledOptions` is `true`, we DO want to show a highlight when disabled options are hovered or keyboard navigated
      {
        conditions: {
          isActive: true,
          isDisabled: true,
          shouldNavigateDisabledOptions: true,
        },
        css: {
          background: '$uiBackgroundHover',
        },
      },
    ],
  }
);

export const OptionGroupView = emotionStyled('div')({
  padding: tokens.ui.select.groupHeader.padding,
});

export const OptionHStack = emotionStyled(HStack)(
  {
    gap: tokens.ui.select.option.spacing,
  },
  {
    variants: {
      verticalOptionAlignment: {
        center: {
          alignItems: 'center',
        },
        top: {
          alignItems: 'flex-start',
        },
      },
    },
  }
);

export const EmptyIconSpacer = emotionStyled('div')({
  height: 16,
  width: 16,
});

export const CheckmarkView = emotionStyled('span')(
  {
    color: '$textPrimary',
  },
  {
    variants: {
      isBranded: {
        true: {
          // Wrap the 16px checkmark in a 24px line-box and center it.
          // Without this, `OptionHStack`'s `alignItems: flex-start` pins the
          // icon to the top of the row while the branded label's glyph sits
          // optically lower inside its 24px line-height — icon appears ~4px
          // too high. Scoped to `isBranded` only so non-branded behavior is
          // unchanged.
          alignItems: 'center',
          display: 'inline-flex',
          height: '$6',
          justifyContent: 'center',
        },
      },
    },
  }
);

export const OptionIconPrefixView = emotionStyled('span')(
  {
    color: '$textSecondary',
  },
  {
    variants: {
      isActive: {
        true: {
          color: '$textPrimary',
        },
      },
      isDisabled: {
        true: {
          color: '$textSecondary',
        },
      },
      isBranded: {
        true: {
          // Same reason as CheckmarkView — center the 16px prefix icon in a
          // 24px line-box so it aligns with the branded label's optical
          // midline instead of pinning to the top of the row.
          alignItems: 'center',
          display: 'inline-flex',
          height: '$6',
          justifyContent: 'center',
        },
      },
    },
  }
);

export const NoteContainerView = emotionStyled('div')(
  {},
  {
    variants: {
      shouldNotMatchTriggerWidth: {
        true: {
          /**
           * CSS technique to prevent note text from influencing dropdown width while
           * ensuring the note container matches its parent's width:
           *
           * - maxWidth: 0 removes this container from parent width calculations.
           *   Any <length> value works, but it must be less than minWidth.
           * - minWidth: '100%' makes the container match its parent's width.
           * - Since min-width > max-width, min-width takes priority per CSS spec.
           *
           * Result: The note wraps within the width determined by the option labels,
           * not the other way around. The container tells its parent "I want to match
           * your size, but don't look to me to determine what that size should be."
           */
          maxWidth: 0,
          minWidth: '100%',
        },
      },
    },
  }
);

export const NoteView = emotionStyled(Note)({
  background: 'transparent',
  padding: '$0.5 $2',
});
